

import colors from "../../utils/colors";
import { createUseStyles } from 'react-jss';

var styles = {
  // General Button types
  button: {
    fontFamily: ['Lato', 'sans-serif'],
    borderRadius: '5px',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    letterSpacing: '0.03rem',
    '&:focus': {
      outline: ['none']
    },
    width: 'max-content',
    cursor: 'pointer'
  },
  inactiveButton: {
    textTransform: 'uppercase',
    cursor: 'not-allowed',
    backgroundColor: colors.neutral[300],
    border: 'none',
    color: colors.neutral.white
  },
  withIcons: {
    justifyContent: 'space-between'
  },
  small: function small(props) {return {
      height: '3.2rem',
      fontSize: '1.2rem',
      lineHeight: '2.0rem',
      padding: {
        left: props.leftIconProp || props.rightIconProp ? '0.6rem' : '2rem',
        right: props.leftIconProp || props.rightIconProp ? '0.6rem' : '2rem'
      },
      width: props.width
    };},
  medium: function medium(props) {return {
      height: '3.6rem',
      fontSize: '1.4rem',
      lineHeight: '2.0rem',
      padding: {
        left: props.leftIconProp || props.rightIconProp ? '1.2rem' : '2.3rem',
        right: props.leftIconProp || props.rightIconProp ? '1.2rem' : '2.3rem'
      },
      width: props.width
    };},
  large: function large(props) {return {
      minWidth: '20rem',
      height: '4.4rem',
      fontSize: '1.4rem',
      lineHeight: '2rem',
      padding: {
        left: '2.3rem',
        right: '2.3rem'
      },
      width: props.width
    };},

  // Contained buttons types
  containedPrimary: {
    color: colors.neutral.white,
    backgroundColor: colors.blue[600],
    boxShadow: {
      x: '0rem',
      y: '0.4rem',
      blur: '0.8rem',
      color: 'rgba(0, 0, 0, 0.14)'
    },
    border: "2px solid ".concat(colors.blue[600]),
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: colors.blue[500],
      borderColor: colors.blue[500],
      color: colors.neutral[100]
    }
  },
  containedDefault: {
    color: colors.neutral[700],
    backgroundColor: colors.neutral[300],
    boxShadow: {
      x: '0px',
      y: '4px',
      blur: '8px',
      color: 'rgba(0, 0, 0, 0.14)'
    },
    border: "2px solid ".concat(colors.neutral[300]),
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: colors.neutral[200],
      borderColor: colors.neutral[200],
      color: colors.neutral[600]
    }
  },

  containedCustom: function containedCustom(props) {return {
      color: props.customTextColor || colors.neutral.white,
      backgroundColor: props.customColor || colors.blue[600],
      boxShadow: {
        x: '0px',
        y: '4px',
        blur: '8px',
        color: 'rgba(0, 0, 0, 0.14)'
      },
      border: "2px solid ".concat(props.customColor || colors.blue[600]),
      textTransform: 'uppercase',
      '&:hover': {
        opacity: '0.7',
        color: colors.neutral[100]
      }
    };},

  // Outlined buttons types
  outlinedPrimary: {
    border: {
      color: colors.blue[600],
      width: '2px',
      style: 'solid'
    },
    textTransform: 'uppercase',
    color: colors.blue[600],
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: colors.blue[600],
      color: colors.neutral.white
    }
  },
  outlinedDefault: {
    border: {
      color: colors.neutral[300],
      width: '2px',
      style: 'solid'
    },
    textTransform: 'uppercase',
    color: colors.neutral[700],
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: colors.neutral[100]
    }
  },
  outlinedCustom: function outlinedCustom(props) {return {
      border: {
        color: props.customColor || colors.blue[600],
        width: '2px',
        style: 'solid'
      },
      textTransform: 'uppercase',
      color: props.customColor || colors.blue[600],
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: props.customColor || colors.blue[600],
        color: colors.neutral.white
      }
    };},

  // Opaque buttons types
  opaquePrimary: {
    border: "2px solid ".concat(colors.blue[100]),
    textTransform: 'uppercase',
    color: colors.blue[500],
    backgroundColor: colors.blue[100],
    '&:hover': {
      borderColor: colors.blue[200],
      backgroundColor: colors.blue[200],
      color: colors.blue[600]
    }
  },
  opaqueDefault: {
    border: "2px solid ".concat(colors.neutral[100]),
    textTransform: 'uppercase',
    color: colors.neutral[500],
    backgroundColor: colors.neutral[100],
    '&:hover': {
      borderColor: colors.neutral[200],
      backgroundColor: colors.neutral[200],
      color: colors.neutral[600]
    }
  },

  // Text buttons types
  textPrimary: {
    backgroundColor: 'transparent',
    color: colors.blue[600],
    border: {
      style: 'none'
    },
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  textDefault: {
    backgroundColor: 'transparent',
    border: {
      style: 'none'
    },
    color: colors.neutral.black,
    '&:hover': {
      textDecoration: 'underline'
    }
  },

  textCustom: function textCustom(props) {return {
      backgroundColor: 'transparent',
      border: {
        style: 'none'
      },
      color: props.customColor || colors.blue[600],
      '&:hover': {
        textDecoration: 'underline'
      }
    };},
  textSmall: {
    fontSize: '1.4rem',
    lineHeight: '2.4rem',
    minWidth: '7.2rem'
  },
  textMedium: {
    fontSize: '1.8rem',
    lineHeight: '2.4rem',
    minWidth: '7.2rem'
  },
  textLarge: {
    fontSize: '2.4rem',
    lineHeight: '3.2rem'
  },

  // Icon buttons types
  iconButton: function iconButton(props) {return {
      width: props.iconSize || '2.4rem',
      height: props.iconSize || '2.4rem'
    };},
  iconPrimary: {
    padding: '0.2rem',
    border: {
      style: 'none'
    },
    color: colors.blue[600],
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: colors.neutral[100]
    }
  },
  iconDefault: {
    padding: '0.2rem',
    border: {
      style: 'none'
    },
    color: colors.neutral.black,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: colors.neutral[100]
    }
  },
  iconCustom: function iconCustom(props) {return {
      padding: '0.2rem',
      backgroundColor: 'transparent',
      border: {
        style: 'none'
      },
      color: props.customColor || colors.blue[600],
      '&:hover': {
        backgroundColor: colors.neutral[100]
      }
    };},

  // Buttons' icons types
  iconProp: function iconProp(props) {return {
      display: 'inline-block',
      verticalAlign: 'bottom',
      width: props.iconSize || '2.4rem',
      height: props.iconSize || '2.4rem'
    };},
  leftIconProp: {
    padding: {
      right: '0.8rem'
    }
  },
  rightIconProp: {
    padding: {
      left: '0.8rem'
    }
  }
};

var createStyles = createUseStyles(styles);

export { createStyles };
//# sourceMappingURL=styles.js.map